var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import jwtDecode from 'jwt-decode';
import React, { useCallback, useEffect, useMemo, useState, } from 'react';
import { createWebAuth } from './auth0';
import { Context } from './context';
import { getUrl } from './getUrl';
export const AuthProvider = ({ domain, clientId, audience = `https://${domain}/api/v1`, signinUrl, accessTokenRenewalThresholdSeconds = 60, children, }) => {
    const auth0 = useMemo(() => createWebAuth({
        domain,
        clientID: clientId,
        audience,
        // The redirect URI is not used anyway in this client, but it's a required option.
        redirectUri: typeof window !== 'undefined'
            ? `${window.location.origin}/auth0-callback/`
            : '/auth0-callback/',
        responseType: 'token id_token',
        scope: 'openid profile email',
    }), [audience, clientId, domain]);
    const [{ loading, error, session, accessToken }, setState] = useState({
        loading: true,
    });
    const refreshAccessToken = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const res = yield new Promise((resolve, reject) => {
                auth0.checkSession({}, (err, result) => err ? reject(err) : resolve(result));
            });
            if (res) {
                // authenticated
                const newAccessToken = {
                    accessToken: res.accessToken,
                    permissions: extractPermissions(res.accessToken),
                    expiresAt: Date.now() + res.expiresIn * 1000,
                };
                setState({
                    loading: false,
                    session: res,
                    accessToken: newAccessToken,
                });
                return newAccessToken;
            }
            // anonymous
            setState({ loading: false });
            return undefined;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (err) {
            setState({
                loading: false,
                error: err.code !== 'login_required' ? err : undefined,
            });
            return undefined;
        }
    }), [auth0]);
    useEffect(() => {
        refreshAccessToken();
    }, [refreshAccessToken]);
    // Refresh the access token before it expires.
    // TODO: This is only necessary for backwards compatibility with the old
    // way of storing the access token in the authentication context. Instead,
    // consumers should use the `getAccessToken()` or the `useAccessToken()` hook.
    useEffect(() => {
        let timer;
        if (accessToken) {
            const delay = Math.max(accessToken.expiresAt -
                Date.now() -
                accessTokenRenewalThresholdSeconds * 1000, 0);
            timer = window.setTimeout(refreshAccessToken, delay);
        }
        return () => {
            if (timer)
                window.clearTimeout(timer);
        };
    }, [accessToken, refreshAccessToken, accessTokenRenewalThresholdSeconds]);
    const getAccessTokenInternal = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!accessToken ||
            (accessToken === null || accessToken === void 0 ? void 0 : accessToken.expiresAt) <
                Date.now() + accessTokenRenewalThresholdSeconds * 1000) {
            return refreshAccessToken();
        }
        return accessToken;
    }), [accessToken, accessTokenRenewalThresholdSeconds, refreshAccessToken]);
    const getAccessToken = useCallback(() => __awaiter(void 0, void 0, void 0, function* () { var _a; return (_a = (yield getAccessTokenInternal())) === null || _a === void 0 ? void 0 : _a.accessToken; }), [getAccessTokenInternal]);
    const context = useMemo(() => createAuthContext({
        auth0,
        signinUrl,
        loading,
        error,
        data: session && accessToken
            ? Object.assign(Object.assign({}, session), { accessToken: accessToken.accessToken, permissions: accessToken.permissions }) : undefined,
        getAccessToken,
    }), [auth0, signinUrl, loading, error, session, accessToken, getAccessToken]);
    return React.createElement(Context.Provider, { value: context }, children);
};
function createAuthContext({ auth0, signinUrl, loading, error, data, getAccessToken, }) {
    if (loading) {
        return {
            auth0,
            loading: true,
            authenticated: false,
            getLogInUrl: getUrl(signinUrl, '/'),
            getSignUpUrl: getUrl(signinUrl, '/signup'),
        };
    }
    if (data) {
        return Object.assign({ auth0, loading: false, authenticated: true, getLogOutUrl: getUrl(signinUrl, '/logout'), getAccessToken }, createAuthenticationData(data));
    }
    return {
        auth0,
        loading: false,
        authenticated: false,
        error,
        getLogInUrl: getUrl(signinUrl, '/'),
        getSignUpUrl: getUrl(signinUrl, '/signup'),
    };
}
function extractPermissions(accessToken) {
    var _a;
    const payload = jwtDecode(accessToken);
    return new Set((_a = payload.permissions) !== null && _a !== void 0 ? _a : []);
}
function createAuthenticationData({ idToken, idTokenPayload: payload, permissions, accessToken, }) {
    return {
        profile: {
            userId: payload[`${payload.iss}userId`],
            email: payload === null || payload === void 0 ? void 0 : payload.email,
            pictureUrl: payload === null || payload === void 0 ? void 0 : payload.picture,
            admin: permissions.has('management'),
            internal: payload[`${payload.iss}internal`],
        },
        permissions,
        idToken,
        accessToken,
    };
}
